import { type KeyboardEvent, type ReactNode, useId, useState } from 'react';
import { formatFull, formatNumber } from '../lib/format.ts';

// Small SVG charts for /stats and /admin. Mark specs: columns ≤ 24px with a 4px rounded
// data end, hairline solid gridlines, one series colour (validated against both
// surfaces), hover/focus tooltips, and a table view for everything.

function niceMax(v: number): number {
  if (v <= 4) return 4;
  const pow = 10 ** Math.floor(Math.log10(v));
  for (const m of [1, 2, 2.5, 5, 10]) if (m * pow >= v) return m * pow;
  return 10 * pow;
}

const shortDay = (day: string) => {
  const [, m, d] = day.split('-').map(Number);
  return `${m}/${d}`;
};

/** Path for a column with rounded top corners and a square base. */
function columnPath(x: number, y: number, w: number, base: number, r = 4): string {
  const h = base - y;
  if (h <= 0) return '';
  const rr = Math.min(r, h, w / 2);
  return `M${x},${base}V${y + rr}Q${x},${y} ${x + rr},${y}H${x + w - rr}Q${x + w},${y} ${x + w},${y + rr}V${base}Z`;
}

export interface Point {
  label: string;
  value: number;
}

/** Single-series column chart over days. */
export function ColumnChart({ data, height = 180, unit, ariaLabel }: { data: Point[]; height?: number; unit: string; ariaLabel: string }) {
  const [hover, setHover] = useState<number | null>(null);
  const [table, setTable] = useState(false);
  const id = useId();
  const width = 640;
  const pad = { top: 12, right: 8, bottom: 24, left: 36 };
  const plotW = width - pad.left - pad.right;
  const plotH = height - pad.top - pad.bottom;
  const max = niceMax(Math.max(0, ...data.map((d) => d.value)));
  const band = plotW / Math.max(1, data.length);
  const barW = Math.min(24, Math.max(2, band - 2));
  const ticks = [0, max / 2, max];
  const base = pad.top + plotH;
  const labelEvery = Math.ceil(data.length / 8);

  const onKey = (e: KeyboardEvent<SVGSVGElement>) => {
    if (e.key === 'ArrowRight') setHover((h) => Math.min(data.length - 1, (h ?? -1) + 1));
    else if (e.key === 'ArrowLeft') setHover((h) => Math.max(0, (h ?? data.length) - 1));
    else return;
    e.preventDefault();
  };

  const h = hover !== null ? data[hover] : null;
  return (
    <div className="viz">
      {table ? (
        <DataTable head={['Day', unit]} rows={data.map((d) => [d.label, formatFull(d.value)])} />
      ) : (
        <div className="viz-plot">
          <svg
            viewBox={`0 0 ${width} ${height}`}
            role="img"
            aria-label={ariaLabel}
            aria-describedby={id}
            tabIndex={0}
            onKeyDown={onKey}
            onBlur={() => setHover(null)}
            onPointerLeave={() => setHover(null)}
          >
            {ticks.map((t) => {
              const y = base - (t / max) * plotH;
              return (
                <g key={t}>
                  <line className="viz-grid" x1={pad.left} x2={width - pad.right} y1={y} y2={y} />
                  <text className="viz-tick" x={pad.left - 6} y={y + 4} textAnchor="end">
                    {formatNumber(t)}
                  </text>
                </g>
              );
            })}
            {data.map((d, i) => {
              const x = pad.left + i * band + (band - barW) / 2;
              const y = base - (d.value / max) * plotH;
              return (
                <g key={d.label} onPointerEnter={() => setHover(i)} onPointerDown={() => setHover(i)}>
                  <rect className="viz-hit" x={pad.left + i * band} y={pad.top} width={band} height={plotH} />
                  <path className={`viz-col ${hover === i ? 'on' : ''}`} d={columnPath(x, y, barW, base)} />
                  {i % labelEvery === (data.length - 1) % labelEvery && (
                    <text className="viz-tick" x={x + barW / 2} y={height - 6} textAnchor="middle">
                      {shortDay(d.label)}
                    </text>
                  )}
                </g>
              );
            })}
            <line className="viz-axis" x1={pad.left} x2={width - pad.right} y1={base} y2={base} />
          </svg>
          {h && hover !== null && (
            <div
              className="viz-tooltip"
              style={{
                left: `${Math.min(90, Math.max(10, ((pad.left + (hover + 0.5) * band) / width) * 100))}%`,
                top: `${((base - (h.value / max) * plotH) / height) * 100}%`,
              }}
            >
              <b>{formatFull(h.value)}</b>
              <span>
                {unit} · {shortDay(h.label)}
              </span>
            </div>
          )}
        </div>
      )}
      <p id={id} className="viz-foot">
        <button className="text-link" onClick={() => setTable((t) => !t)}>
          {table ? 'Show chart' : 'Show table'}
        </button>
      </p>
    </div>
  );
}

export function Sparkline({ values, width = 96, height = 28 }: { values: number[]; width?: number; height?: number }) {
  if (values.length < 2) return null;
  const max = Math.max(1, ...values);
  const step = width / (values.length - 1);
  const pts = values.map((v, i) => [i * step, height - 3 - (v / max) * (height - 6)] as const);
  const last = pts[pts.length - 1];
  return (
    <svg className="spark" viewBox={`0 0 ${width} ${height}`} width={width} height={height} aria-hidden>
      <polyline points={pts.map((p) => p.join(',')).join(' ')} />
      <circle cx={last[0]} cy={last[1]} r={3} />
    </svg>
  );
}

/** Stat tile: label, value, delta vs the previous period, 14-point trend. */
export function StatTile({ label, value, previous, trend, hint }: { label: string; value: number; previous?: number; trend?: number[]; hint?: string }) {
  const delta = previous === undefined ? null : value - previous;
  return (
    <div className="stat-tile" title={hint}>
      <span className="stat-label">{label}</span>
      <b className="stat-value">{formatNumber(value)}</b>
      <span className="stat-foot">
        {delta !== null && (
          <span className={`delta ${delta > 0 ? 'up' : delta < 0 ? 'down' : ''}`}>
            {delta > 0 ? '▲' : delta < 0 ? '▼' : '•'} {formatNumber(Math.abs(delta))} <small>vs yesterday</small>
          </span>
        )}
        {trend && <Sparkline values={trend} />}
      </span>
    </div>
  );
}

/** Ordered stages (visitor → player → finished → account) as horizontal bars. */
export function Funnel({ stages }: { stages: { label: string; value: number }[] }) {
  const top = Math.max(1, stages[0]?.value ?? 1);
  return (
    <ol className="funnel">
      {stages.map((s, i) => (
        <li key={s.label} className={`funnel-step step-${i}`}>
          <span className="funnel-label">{s.label}</span>
          <span className="funnel-track">
            <span className="funnel-bar" style={{ width: `${Math.max(1.5, (s.value / top) * 100)}%` }} />
          </span>
          <span className="funnel-value">
            <b>{formatFull(s.value)}</b>
            {i > 0 && <small>{Math.round((s.value / top) * 100)}%</small>}
          </span>
        </li>
      ))}
    </ol>
  );
}

/** A ranked table with an inline bar for the first numeric column. */
export function BarTable({ head, rows }: { head: [string, string, string?]; rows: { key: ReactNode; value: number; extra?: ReactNode }[] }) {
  const max = Math.max(1, ...rows.map((r) => r.value));
  if (!rows.length) return <p className="muted small">No data yet.</p>;
  return (
    <table className="table bar-table">
      <thead>
        <tr>
          <th>{head[0]}</th>
          <th>{head[1]}</th>
          {head[2] && <th>{head[2]}</th>}
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i}>
            <td>{r.key}</td>
            <td className="bar-cell">
              <span className="inline-bar" style={{ width: `${(r.value / max) * 100}%` }} />
              <span className="num">{formatFull(r.value)}</span>
            </td>
            {head[2] && <td className="num">{r.extra}</td>}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function DataTable({ head, rows }: { head: string[]; rows: (string | number)[][] }) {
  return (
    <table className="table">
      <thead>
        <tr>
          {head.map((h) => (
            <th key={h}>{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i}>
            {r.map((c, j) => (
              <td key={j} className={typeof c === 'number' ? 'num' : ''}>
                {c}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}
